import React, { useContext, useState, useEffect, useRef } from 'react';
import { Formik } from 'formik';
import { Environment } from 'src/Environment';
import { Form, Field } from 'formik';
import { Redirect } from 'react-router-dom';

import { agentLogin } from 'src/services/agent-login.service';
import toastMessageServiceSingleton from 'src/services/toast-message.service';
import {
  onboardingLetsGetStartedRoute,
  onboardingContinueReferenceRoute,
} from 'src/routing/routes';
import { hasNoSpecialCharacters } from 'src/helpers/validation';
import { FloatingInput, ShapesDecorativeBorder } from 'src/components';
import { MiniAppFocusWrapper } from 'src/components/miniapp-focus-wrapper/miniapp-focus-wrapper';
import { SalesforceContext, CustomerTypes } from 'src/context/salesforce-context';
import TealiumEvents from 'src/helpers/tealium-events';
import { OnboardingFormBackground } from '../onboarding-form-background/onboarding-form-background';
import useTechnicalErrorHandler from 'src/hooks/useTechnicalError';
import './agent-login.scss';

export default function AgentLogin() {
  const [inputDanger, setInputDanger] = useState({
    agentId: false,
  });
  const [error, setError] = useState('Please complete all the required fields.');
  const [loginError, setLoginError] = useState('');
  const [redirectToRoute, setRedirectToRoute] = useState('');
  const { clearCache, setCustomerType, lead, setLead, save, customerType } =
    useContext(SalesforceContext);
  const topOfPage = useRef('top');
  const handleTechnicalError = useTechnicalErrorHandler();

  useEffect(() => {
    TealiumEvents.agentLoginPageLoad();
    clearCache();
    if (topOfPage.current) {
      topOfPage.current.scrollIntoView(false);
    }
    setCustomerType(CustomerTypes.agent);
  }, []);

  if (!!redirectToRoute) {
    return <Redirect push to={redirectToRoute} />;
  }

  const updateLead = (newLead) => {
    setLead({
      ...lead,
      agentId: newLead.agentId,
    });
  };

  const continueLink = () => (
    <div className="center-text back-link">
      <a
        onClick={() => {
          TealiumEvents.agentLoginPageClickContinueApplication(onboardingContinueReferenceRoute);
          setRedirectToRoute(onboardingContinueReferenceRoute);
        }}
        automationid="Onboarding_Agent_Login_Anchor_Link_Continue_Your_Application"
      >
        Go to saved application
        <span className="icon-menu-chevron-right has-padding-left-6 is-size-7" />
      </a>
    </div>
  );

  const formContent = () => {
    return (
      <div className="form">
        <section className="agent-login-form">
          <Formik
            initialValues={{
              agentId: '',
              password: '',
            }}
            validate={validateForm}
            onSubmit={submitForm}
            validateOnChange={true}
            validateOnBlur={false}
            enableReinitialize={true}
          >
            {({ isSubmitting }) => (
              <Form>
                <div className="background-container">
                  <div className="top-background-container">
                    <div className="columns is-gapless is-centered is-mobile">
                      <div className="column is-four-fifths">
                        <div className="has-text-centered has-padding-top-2">
                          <p className="heading-four has-text-purple-grey" ref={topOfPage}>
                            Login to your account
                          </p>
                        </div>
                        <Field
                          name="agentId"
                          type="text"
                          placeholder="Agent ID"
                          component={MiniAppFocusWrapper}
                          innerComponent={FloatingInput}
                          fieldType="standard"
                          className="top-field"
                          spacing="has-margin-top-5"
                          automationid="Onboarding_Agent_Login_Field_First_Names"
                          autoFocus
                        />
                        <Field
                          name="password"
                          type="password"
                          component={MiniAppFocusWrapper}
                          innerComponent={FloatingInput}
                          className="bottom-field"
                          fieldType="standard"
                          placeholder="Password"
                          automationid="Onboarding_Agent_Login_Password"
                        />
                        <p className="has-text-primary has-text-center has-padding-y-4">
                          {loginError}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="columns is-mobile is-centered">
                    <div className="column is-four-fifths">
                      <div className="columns is-mobile has-padding-y-5">
                        <div className="column is-full">
                          <button
                            className="button is-primary is-rounded is-fullwidth button-text has-margin-top-6"
                            type="submit"
                            automationid="Onboarding_Agent_login_Button_Continue"
                            disabled={isSubmitting || error}
                          >
                            Login
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {!Environment.current?.disabledFeatures.onboardingContinueApplication
                  ? continueLink()
                  : null}
              </Form>
            )}
          </Formik>
        </section>
      </div>
    );
  };

  return (
    <div className="agent-login">
      <OnboardingFormBackground
        isAgent={true}
        customerType={customerType}
        setCustomerType={setCustomerType}
      >
        <ShapesDecorativeBorder>{formContent()}</ShapesDecorativeBorder>
      </OnboardingFormBackground>
    </div>
  );

  async function submitForm(values, helpers) {
    TealiumEvents.agentLoginClickAndFormEndCombined(window.location.href);
    const result = await agentLogin(values.agentId, values.password, false);
    if (!result?.ok && result.credentialError) {
      toastMessageServiceSingleton.addError(
        'Invalid username or password',
        null,
        '401',
        true,
        null,
        true,
      );
      return;
    } else if (!result || !result.ok) {
      handleTechnicalError('unrecognized error received in response to login request');
      return;
    }
    setLoginError('');
    const { setSubmitting } = helpers;
    setSubmitting(false);
    save();
    setRedirectToRoute(onboardingLetsGetStartedRoute);
  }

  function validateForm(values) {
    const errors = {};
    let newInputDanger = inputDanger;

    const invalidAgentId = !hasNoSpecialCharacters(values.agentId);
    newInputDanger['agentId'] = invalidAgentId;
    if (invalidAgentId) {
      errors.agentId = values.agentId ? 'The agent id entered is invalid' : null;
    }

    setInputDanger(newInputDanger);
    let fieldsNotCompleted = !values.agentId || !values.password;

    if (fieldsNotCompleted) {
      setError('Please complete all the required fields.');
    } else if (errors.agentId) {
      setError('Please enter a valid agent Id');
    } else {
      setError('');
    }

    updateLead(values);

    return errors;
  }
}
